/**
 * Wire adapter: `PatientProfileAppointmentCandidates`
 * (`@heidi/session/contracts`) -> `SuggestedMatchCandidates`
 * (`@heidi/patients`).
 *
 * App composition for the session->patients join; the domain DAG refuses this
 * edge by design — this adapter is the boundary contract.
 *
 * `domainAllowList('patients')` grants `patients` + `shared` + `workspace`, so
 * `@heidi/patients` may not name the session door. The app layer is the one place
 * both are importable, which is why the mapping lives here and why its own
 * compilation is what reports a rename or a removal on either side.
 *
 * Both union arms are mapped, so the ranked list keeps its length and its order.
 * The appointment arm carries `appointment_record_id` on the wire and nothing but
 * its discriminant on the view: the surface skips appointment rows until
 * appointment display data exists, and narrowing the arm here is what says so.
 * `extracted_field_matches` and `confidence` are on the wire and are not on the
 * view, because nothing the surface renders reads them.
 */
import type { SuggestedMatchCandidate, SuggestedMatchCandidates } from '@heidi/patients';
import type {
  PatientProfileAppointmentCandidate,
  PatientProfileAppointmentCandidates,
} from '@heidi/session/contracts';

function toSuggestedMatchCandidate(
  wire: PatientProfileAppointmentCandidate
): SuggestedMatchCandidate {
  if (wire.entity_type === 'patient_profile') {
    return {
      entity_type: 'patient_profile',
      patient_profile_id: wire.patient_profile_id,
    };
  }
  return { entity_type: 'appointment' };
}

export function toSuggestedMatchCandidates(
  wire: PatientProfileAppointmentCandidates
): SuggestedMatchCandidates {
  return {
    ranked_patient_profile_or_appointment: wire.ranked_patient_profile_or_appointment.map(
      toSuggestedMatchCandidate
    ),
  };
}
